import Card from '@jarvis.oddle/core/components/Card'
import Flex from '@jarvis.oddle/core/components/Flex'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import {
  borderRadiusSizes,
  margin,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'

import { isObjectEmpty } from 'src/utils/object'
import React, { useEffect } from 'react'
import styled from 'styled-components'
import { getCreditCardInfo } from 'tools/payment'
import { darken, isLight, lighten } from 'src/utils/style'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectThemeSettingData } from 'src/redux/customisation'
import { selectSavedCreditCardAction } from 'src/redux/checkout'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import { H4, LinkText, P } from '@jarvis.oddle/core/components/Typo'
import { selectDetailMenuSite } from 'src/redux/meta'
import modalController from 'src/decorators/ModalController'
import { newrelicTrackUserAction } from 'src/utils/newrelicLogger'

const StyledLinkText = styled(LinkText)`
  margin-top: ${margin[marginSizes.extraSmall]};
  white-space: nowrap;
`

const Container = styled(Card).attrs(() => ({
  borderRadius: borderRadiusSizes.small,
  justifyContent: 'space-between',
  alignItems: 'center'
}))`
  display: flex;
  min-height: 60px;
  background-color: ${props => props.colorBackground};
  cursor: ${props => props.showRadioButton ? 'pointer' : 'default'};

  &&& {
    border: none;
    padding: ${padding[paddingSizes.small]};
  }
`

const CardNameContainer = styled(Flex).attrs(() => ({
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center'
}))`
  flex: 0 1 auto;
  margin-right: ${margin[marginSizes.medium]};
  margin-left: 7px;
`

const StyledRadio = styled('svg')`
  color: ${props => props.fill};
  height: 3em;
  margin: 0;
  margin-inline-end: 0.75rem;
  width: 3em;
`

const SelectedSavedCreditCard = ({
  selectedSavedCreditCard,
  iSelected,
  onSelectSavedCreditCard,
  showRadioButton
}: {
  selectedSavedCreditCard: Oms.V4.SelectedSavedCreditCard
  iSelected?: boolean
  onSelectSavedCreditCard?: () => void
  showRadioButton?: boolean
}): JSX.Element => {
  const { t } = React.useContext(TranslationContext)
  const customisationState = useAppSelector(selectThemeSettingData)
  const { settings: { colorContainerColor, colorBodyFont } } = customisationState
  const dispatch = useAppDispatch()
  const meta = useAppSelector(selectDetailMenuSite)
  const { oddlePassEnabled, newOddlePassFlow } = meta

  const defaultCreditCard = React.useMemo(
    () => ({
      ...selectedSavedCreditCard,
      ...(selectedSavedCreditCard.card
        ? getCreditCardInfo(selectedSavedCreditCard.card.brand)
        : {})
    }),
    [selectedSavedCreditCard]
  )

  const onClickManageCards = () => {
    if (window.opw && meta?.stripeToken) {
      newrelicTrackUserAction('checkout_click_manage_saved_card_from_op')
      window.opw.manageCreditCard(meta?.stripeToken, customisationState.settings)
    } else {
      newrelicTrackUserAction('checkout_click_manage_saved_card_from_old_flow')
      modalController.showModal('CreditCardChooserModal', {
        onSelect: onSelectSavedCreditCard
      })
    }
  }

  useEffect(() => {
    if (window.opw && oddlePassEnabled && newOddlePassFlow) {
      window.opw.addListener(async (action: string, message: Oms.V4.SelectedSavedCreditCard) => {
        if (action === 'selected_saved_credit_card') {
          newrelicTrackUserAction('checkout_selected_card_from_saved_cards')
          dispatch(selectSavedCreditCardAction(message))
          return onSelectSavedCreditCard()
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSelectUsingSavedCard = () => {
    if (showRadioButton) {
      dispatch(selectSavedCreditCardAction(selectedSavedCreditCard))
      return onSelectSavedCreditCard()
    }
  }

  return (
    <React.Fragment>
      <Flex
        flexDirection='row'
        justifyContent='space-between'
        alignItems='center'
      >
        <P bold>{t('Frequently Used Card')}</P>
        <Flex flexDirection='row' alignItems='center'>
          <StyledLinkText onClick={onClickManageCards} heading>
            {t('Select Another Card')}
          </StyledLinkText>
        </Flex>
      </Flex>
      <Container
        data-testid='paymentTypeCard'
        colorBackground={isLight(colorContainerColor) ? darken(colorContainerColor, 10) : lighten(colorContainerColor, 15)}
        showRadioButton={showRadioButton}
        onClick={handleSelectUsingSavedCard}
      >
        {!isObjectEmpty(defaultCreditCard?.card || {}) && (
          <React.Fragment>
            <CardNameContainer>
              {
                showRadioButton && (
                  <StyledRadio xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill={colorBodyFont} focusable="false" role="presentation">
                    <path
                      d="M14.25 8C14.25 11.4518 11.4518 14.25 8 14.25C4.54822 14.25 1.75 11.4518 1.75 8C1.75 4.54822 4.54822 1.75 8 1.75C11.4518 1.75 14.25 4.54822 14.25 8Z"
                      stroke="currentColor" strokeWidth="1.5" fill="none"/>
                    {
                      iSelected && <circle cx="8" cy="8" r="3.5" fill={colorBodyFont}/>
                    }
                  </StyledRadio>
                )
              }
              <Flex marginRight={marginSizes.medium}>
                <defaultCreditCard.icon width={41} height={41} />
              </Flex>
              <H4 normalised textAlign='left' color={colorBodyFont}>
                •••• {defaultCreditCard.card.last4}
              </H4>
            </CardNameContainer>
          </React.Fragment>
        )}
      </Container>
    </React.Fragment>
  )
}

export default SelectedSavedCreditCard
